import React, { useRef } from 'react';
import classnames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/splide/dist/css/themes/splide-default.min.css';

import Image from 'components/Common/Medias/Image';
import { env } from 'settings/env';
import { MOBILE } from 'settings/mediaQuery';
import { DefaultCarouselProductType } from 'types/Carousel';

import styles from './LinkedProductsCarousel.module.scss';

type Props = {
  /** Main product image url */
  productImg: string;
  /** Title of the carousel */
  title?: string;
  /** List of products */
  products: DefaultCarouselProductType[];
  /** Function when product is clicked */
  onClickProduct?: () => void;
  /** Additional class to carousel */
  classes?: {
    wrapper?: string;
  };
};

/**
 * Display a list of linked products in carousel
 */
export default function LinkedProductsCarousel({
  title,
  productImg,
  products,
  onClickProduct,
  classes,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const ref = useRef<Splide>(null);
  const isMobile = useMediaQuery({ query: MOBILE });

  if (!products?.length) {
    return null;
  }

  const firstSlideItems = products.length > 4 ? products.slice(0, 3) : products;
  const secondSlideItems = products.length > 4 ? products.slice(3, 6) : null;

  return (
    <div className={classnames(styles.container, classes?.wrapper)}>
      {title && <h2 className={styles.title}>{title}</h2>}
      <div className={styles.carouselContainer}>
        <Image
          alt={productImg}
          className={styles.mainImage}
          src={appendBaseImageUrl(productImg)}
        />

        <Splide
          options={{
            width: isMobile ? '100%' : 440,
            gap: '10px',
            arrows: false,
            pagination: false,
          }}
          tag="div"
          ref={ref}
        >
          <SplideSlide>
            <div className={styles.suggestedProducts}>
              {firstSlideItems.map(item => (
                <ProductLink
                  key={item.productId}
                  product={item}
                  updateImageSrc={appendBaseImageUrl}
                />
              ))}

              {secondSlideItems && (
                <button
                  className={styles.navigateButton}
                  onClick={() => ref.current?.splide?.go(1)}
                >
                  {t('LABEL_SHOW_MORE')}
                </button>
              )}
            </div>
          </SplideSlide>

          {secondSlideItems && (
            <SplideSlide>
              <div className={styles.suggestedProducts}>
                {secondSlideItems.map(item => (
                  <ProductLink
                    key={item.productId}
                    product={item}
                    onClickProduct={onClickProduct}
                    updateImageSrc={appendBaseImageUrl}
                  />
                ))}
                <button
                  className={classnames(styles.navigateButton, styles.prev)}
                  onClick={() => ref.current?.splide?.go(0)}
                >
                  {t('LABEL_SHOW_LESS')}
                </button>
              </div>
            </SplideSlide>
          )}
        </Splide>
      </div>
    </div>
  );
}

function ProductLink({
  product: { url, shortLabel, visuelFace, metaMarque },
  onClickProduct,
  updateImageSrc,
}: {
  product: DefaultCarouselProductType;
  onClickProduct?: () => void;
  sendEvent?: (sku: string, name: string) => void;
  updateImageSrc?: (image: string) => string;
}) {
  const imageSrc = updateImageSrc ? updateImageSrc(visuelFace) : visuelFace;

  return (
    <Link className={styles.productLink} to={url} onClick={onClickProduct}>
      <div className={styles.productItem}>
        <Image alt={shortLabel} src={imageSrc} />
        <span className={styles.productTitle}>
          {metaMarque ? `${metaMarque}® - ${shortLabel}` : shortLabel}
        </span>
      </div>
    </Link>
  );
}

function appendBaseImageUrl(imagePath: string): string {
  return env.ENV_TWICPICS_URL_MEDIA + imagePath;
}
